import React from 'react';

// Calculation Modal Component
export const CalculationModal = ({
  showCalculationModal,
  setShowCalculationModal,
  calculationData
}) => {
  if (!showCalculationModal || !calculationData) return null;

  const displayData = calculationData;

  // Calculate activation rate if activation count is available
  const activationCount = displayData.calculations?.find(calc => calc.year === 1)?.activationCount ||
    displayData.calculations?.reduce((sum, calc) => sum + (calc.activationCount || 0), 0) || 0;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-gray-900">Detailed Commission Calculation Breakdown</h3>
            <button
              onClick={() => setShowCalculationModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg border-l-4 border-blue-500">
              <h4 className="text-2xl font-bold text-gray-800 mb-6">Commission Calculation Summary</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-blue-600">₹{calculationData.totalRevenue?.toLocaleString('en-IN')}</div>
                  <div className="text-sm text-gray-600">Total Revenue</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-green-600">₹{calculationData.totalCommission?.toLocaleString('en-IN')}</div>
                  <div className="text-sm text-gray-600">Total Commission</div>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="text-3xl font-bold text-purple-600">{activationCount}</div>
                  <div className="text-sm text-gray-600">Active Members</div>
                </div>
              </div>
              <div className="mt-4 text-sm text-gray-700">
                <div className="flex items-center space-x-4">
                  <span><strong>Leader:</strong> {calculationData.leaderName}</span>
                  <span><strong>Period:</strong> {calculationData.month}</span>
                </div>
              </div>
            </div>

            {/* Policy Contributions Table */}
            {calculationData.policyContributions && calculationData.policyContributions.length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-800 mb-4">📋 Policy Contributions</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full table-auto border-collapse">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Policy No</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Advisor</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Duration</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Total Premium</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Annual Premium</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Installment Year</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Contribution</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold text-gray-700">Qualified</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calculationData.policyContributions.map((policy, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">{policy.policyNo}</td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">{policy.advisorName}</td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">{policy.durationYears} Years</td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">₹{policy.sumInsured?.toLocaleString('en-IN')}</td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">₹{policy.annualPremium?.toLocaleString('en-IN')}</td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">{policy.installmentYear}</td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">₹{policy.contributionAmount?.toLocaleString('en-IN')}</td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${policy.qualifiedForActivation ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                              {policy.qualifiedForActivation ? 'Yes' : 'No'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Year-wise Premium Breakdown */}
            {calculationData.yearWisePolicyBreakdown && Object.keys(calculationData.yearWisePolicyBreakdown).length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h4 className="text-xl font-bold text-gray-800 mb-4">📊 Year-wise Premium Breakdown</h4>
                <div className="space-y-4">
                  {Object.entries(calculationData.yearWisePolicyBreakdown).map(([year, data]) => (
                    <div key={year} className="border border-gray-200 rounded-lg p-4">
                      <h5 className="text-lg font-semibold text-gray-700 mb-3">Year {year}</h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="text-lg font-bold text-blue-600">₹{data.totalRevenue?.toLocaleString('en-IN')}</div>
                          <div className="text-sm text-blue-700">Total Revenue</div>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <div className="text-lg font-bold text-green-600">{(data.commissionRate * 100).toFixed(1)}%</div>
                          <div className="text-sm text-green-700">Commission Rate</div>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg">
                          <div className="text-lg font-bold text-purple-600">₹{data.commissionAmount?.toLocaleString('en-IN')}</div>
                          <div className="text-sm text-purple-700">Commission Amount</div>
                        </div>
                      </div>
                      {data.policies && data.policies.length > 0 && (
                        <div className="mt-4">
                          <h6 className="text-sm font-medium text-gray-600 mb-2">Contributing Policies:</h6>
                          <div className="overflow-x-auto">
                            <table className="min-w-full table-auto border-collapse text-sm">
                              <thead>
                                <tr className="bg-gray-50">
                                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Policy No</th>
                                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Customer</th>
                                  <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">Advisor</th>
                                  <th className="border border-gray-300 px-3 py-2 text-right font-medium text-gray-700">Annual Premium</th>
                                  <th className="border border-gray-300 px-3 py-2 text-right font-medium text-gray-700">Contribution</th>
                                </tr>
                              </thead>
                              <tbody>
                                {data.policies.map((policy, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">{policy.policyNo}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">{policy.customerName}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-gray-900">{policy.advisorName}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-right text-gray-900">₹{policy.annualPremium?.toLocaleString('en-IN')}</td>
                                    <td className="border border-gray-300 px-3 py-2 text-right text-gray-900">₹{policy.contributionAmount?.toLocaleString('en-IN')}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      {data.calculationSteps && data.calculationSteps.length > 0 && (
                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                          <h6 className="text-sm font-medium text-gray-600 mb-2">Calculation Steps:</h6>
                          <div className="space-y-1">
                            {data.calculationSteps.map((step, index) => (
                              <div key={index} className="text-sm text-gray-700 font-mono">{step}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Commission Rate Application */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h4 className="text-xl font-bold text-gray-800 mb-4">💰 Commission Rate Application</h4>
              <div className="space-y-4">
                {calculationData.calculations && calculationData.calculations.length > 0 ? (
                  calculationData.calculations.map((calc, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-3">
                        <h5 className="text-lg font-semibold text-gray-700">
                          {calc.year === 1 ? 'Year 1 (Activation-Based)' : `Year ${calc.year} (Renewal)`}
                        </h5>
                        <div className="text-right">
                          <div className="text-xl font-bold text-green-600">₹{calc.commission?.toLocaleString('en-IN')}</div>
                          <div className="text-sm text-gray-600">{calc.rate}</div>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <div className="text-lg font-bold text-blue-600">₹{calc.revenue?.toLocaleString('en-IN')}</div>
                          <div className="text-sm text-blue-700">Revenue</div>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <div className="text-lg font-bold text-green-600">{calc.rate}</div>
                          <div className="text-sm text-green-700">Rate Applied</div>
                        </div>
                      </div>
                      {calc.note && (
                        <div className="mt-3 p-2 bg-yellow-50 rounded text-sm text-yellow-800">
                          <strong>Note:</strong> {calc.note}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No detailed calculation data available for this commission.</p>
                    <p className="text-sm mt-2">Total Commission: ₹{calculationData.totalCommission?.toLocaleString('en-IN')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Total Commission Calculation */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border-l-4 border-green-500">
              <h4 className="text-2xl font-bold text-gray-800 mb-6">✅ Total Commission Calculation</h4>
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-lg border">
                  <h5 className="text-lg font-semibold text-gray-800 mb-3">Step-by-Step Calculation</h5>
                  <div className="space-y-2 text-sm font-mono">
                    {calculationData.calculations && calculationData.calculations.map((calc, index) => (
                      <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                        <span>Year {calc.year}: ₹{calc.revenue?.toLocaleString('en-IN')} × {calc.rate}</span>
                        <span className="font-bold text-green-600">₹{calc.commission?.toLocaleString('en-IN')}</span>
                      </div>
                    ))}
                    <div className="border-t-2 border-gray-300 pt-2 mt-4">
                      <div className="flex justify-between items-center p-2 bg-green-100 rounded font-bold text-lg">
                        <span>Total Commission:</span>
                        <span className="text-green-600">₹{calculationData.totalCommission?.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Upline Distribution Cascade / Downline Commission Received */}
            {calculationData.uplineCascade && calculationData.uplineCascade.length > 0 && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border-l-4 border-purple-500">
                <h4 className="text-xl font-bold text-gray-800 mb-4">
                  {calculationData.leaderName === 'Super Admin' ? '📈 Downline Commission Received' : '🔄 Upline Distribution Cascade'}
                </h4>
                <div className="space-y-3">
                  {calculationData.uplineCascade.map((level, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h5 className="text-lg font-semibold text-gray-700">
                          {calculationData.leaderName === 'Super Admin'
                            ? `${level.leaderName} (Direct Leader)`
                            : `Level ${level.level}: ${level.leaderName}`
                          }
                        </h5>
                        <div className="text-right">
                          <div className="text-xl font-bold text-purple-600">₹{level.shareAmount?.toLocaleString('en-IN')}</div>
                          <div className="text-sm text-gray-600">
                            {calculationData.leaderName === 'Super Admin'
                              ? `(${level.sharePercentage}% of ${level.leaderName}'s commission)`
                              : `(${level.sharePercentage}% share)`
                            }
                          </div>
                        </div>
                      </div>
                      <div className="text-sm text-gray-600">
                        <div>
                          {calculationData.leaderName === 'Super Admin'
                            ? `${level.leaderName}'s Commission: ₹${level.sourceAmount?.toLocaleString('en-IN')}`
                            : `Source Amount: ₹${level.sourceAmount?.toLocaleString('en-IN')}`
                          }
                        </div>
                        <div className="font-mono text-xs mt-1">{level.calculation}</div>
                        <div className="text-xs mt-1 text-gray-500">
                          {calculationData.leaderName === 'Super Admin'
                            ? `Commission received from ${level.leaderName}'s team performance`
                            : level.sourceDetail
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Leader Commission Journey */}
            {calculationData.leaderCommissionJourney && (
              <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-6 rounded-lg border-l-4 border-indigo-500">
                <h4 className="text-xl font-bold text-gray-800 mb-4">🎯 {calculationData.leaderName}'s Commission Journey</h4>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="text-lg font-semibold text-gray-800 mb-3">Commission Sources</h5>
                    <div className="space-y-3">
                      {calculationData.leaderCommissionJourney.sources?.map((source, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex justify-between items-center mb-2">
                            <span className="font-medium text-gray-700">{source.source}</span>
                            <span className="text-lg font-bold text-green-600">₹{source.amount?.toLocaleString('en-IN')}</span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <div>Flow: {source.flow}</div>
                            <div className="text-xs text-gray-500 mt-1">{source.reason}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white p-4 rounded-lg border">
                    <h5 className="text-lg font-semibold text-gray-800 mb-3">Distribution to Upline</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-green-50 p-3 rounded-lg text-center">
                        <div className="text-xl font-bold text-green-600">₹{calculationData.leaderCommissionJourney.totalReceived?.toLocaleString('en-IN')}</div>
                        <div className="text-sm text-green-700">Total Received</div>
                      </div>
                      <div className="bg-red-50 p-3 rounded-lg text-center">
                        <div className="text-xl font-bold text-red-600">₹{calculationData.leaderCommissionJourney.distribution?.uplineAmount?.toLocaleString('en-IN')}</div>
                        <div className="text-sm text-red-700">Upline Share</div>
                      </div>
                      <div className="bg-blue-50 p-3 rounded-lg text-center">
                        <div className="text-xl font-bold text-blue-600">₹{calculationData.leaderCommissionJourney.distribution?.netAmount?.toLocaleString('en-IN')}</div>
                        <div className="text-sm text-blue-700">Net Amount</div>
                      </div>
                    </div>
                    <div className="mt-3 p-2 bg-gray-50 rounded text-sm font-mono">
                      {calculationData.leaderCommissionJourney.distribution?.calculation}
                    </div>
                  </div>
                </div>
              </div>
            )}

          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={() => setShowCalculationModal(false)}
              className="px-6 py-3 bg-gray-500 text-white rounded-md hover:bg-gray-600 font-medium"
            >
              Close Detailed View
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Payment Processing Modal Component
export const PaymentModal = ({
  processingPayment,
  setProcessingPayment,
  transactionData,
  setTransactionData,
  handlePaymentInputChange,
  handleProcessPayment
}) => {
  if (!processingPayment) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Process Commission Payment</h3>
            <button
              onClick={() => {
                setProcessingPayment(null);
                setTransactionData({
                  transaction_ref: '',
                  payment_method: 'Bank Transfer',
                  payment_date: '',
                  notes: ''
                });
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={transactionData.transaction_ref}
                onChange={(e) => handlePaymentInputChange('transaction_ref', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter transaction ID (e.g., TXN123456789)"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Unique identifier for this payment transaction</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <select
                value={transactionData.payment_method}
                onChange={(e) => handlePaymentInputChange('payment_method', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="Cheque">Cheque</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
                <option value="IMPS">IMPS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={transactionData.payment_date || ''}
                onChange={(e) => handlePaymentInputChange('payment_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Date when the payment was made</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <textarea
                value={transactionData.notes}
                onChange={(e) => handlePaymentInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Additional notes about this payment..."
                rows="3"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => {
                setProcessingPayment(null);
                setTransactionData({
                  transaction_ref: '',
                  payment_method: 'Bank Transfer',
                  payment_date: '',
                  notes: ''
                });
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => handleProcessPayment(processingPayment)}
              disabled={!transactionData.transaction_ref.trim()}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Process Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Approval Modal Component
export const ApprovalModal = ({
  approvingPayout,
  setApprovingPayout,
  transactionData,
  setTransactionData,
  handlePaymentInputChange,
  handleConfirmApproval
}) => {
  if (!approvingPayout) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Approve Commission Payout</h3>
            <button
              onClick={() => {
                setApprovingPayout(null);
                setTransactionData({
                  transaction_ref: '',
                  payment_method: 'Bank Transfer',
                  notes: ''
                });
              }}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
              </svg>
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Transaction ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={transactionData.transaction_ref}
                onChange={(e) => handlePaymentInputChange('transaction_ref', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter transaction ID (e.g., TXN123456789)"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Unique identifier for this payment transaction</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
              <select
                value={transactionData.payment_method}
                onChange={(e) => handlePaymentInputChange('payment_method', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="UPI">UPI</option>
                <option value="Cheque">Cheque</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
                <option value="IMPS">IMPS</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={transactionData.payment_date || ''}
                onChange={(e) => handlePaymentInputChange('payment_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Date when the payment was made</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notes (Optional)</label>
              <textarea
                value={transactionData.notes}
                onChange={(e) => handlePaymentInputChange('notes', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Additional notes about this payment..."
                rows="3"
              />
            </div>
          </div>

          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => {
                setApprovingPayout(null);
                setTransactionData({
                  transaction_ref: '',
                  payment_method: 'Bank Transfer',
                  payment_date: '',
                  notes: ''
                });
              }}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={() => handleConfirmApproval(false)}
              disabled={!transactionData.transaction_ref.trim() || !transactionData.payment_date}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              Approve & Process Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};